import type { AuthService } from "@storytell/auth";
import type {
  ActiveContextStore,
  AssetsService,
  BillingService,
  ChatService,
  ConceptsService,
  ConnectivityService,
  FeatureGateService,
  GrantsService,
  KnowledgeUIService,
  LabelsService,
  NavigationService,
  NotificationsService,
  ProjectsService,
  RelsService,
  StoredPromptsService,
  SuggestionsService,
  TenantsStore,
  UIStore,
  UserContextService,
  UserStore,
  ViewsService,
} from "@storytell/core";
import type { DeviceService } from "@storytell/device";
import type { LocaleService } from "@storytell/locale";
import type { Logger } from "@storytell/logger";
import { Noop as NoopLogger } from "@storytell/logger";
import type { StreamingService } from "@storytell/streaming";
import type { TenantSummary } from "@storytell/types";
import type { ActionsService } from "./services/ActionsService";
import type { ArtifactsService } from "./services/ArtifactsService";
import type { ChatTitleService } from "./services/chatTitleService";
import type { DevicesService } from "./services/DevicesService";
import type { LiveSessionsService } from "./services/LiveSessionsService";
import type { MemoryService } from "./services/MemoryService";
import type { ProjectBillingService } from "./services/ProjectBillingService";
import type { RecentChatsService } from "./services/RecentChatsService";
import type { SkillsService } from "./services/SkillsService";
import type { TokensService } from "./services/tokens";

/** Safe placeholder so code never calls missing methods if context defaults before Provider mounts. */
const defaultUserContext: UserContextService = {
  get: () => undefined,
  activeContext: () => undefined,
  canAt: () => false,
  switchToProject: () => undefined,
  recordRecentProject: () => undefined,
  forgetRecentProject: () => undefined,
  getRecentProjectIds: () => [],
};

const defaultActiveContext: ActiveContextStore = {
  refetch: () => Promise.resolve(undefined),
  activeContext: () => undefined,
  canAt: () => false,
  isLoadedForCurrentScope: () => false,
};

const defaultUserStore: UserStore = {
  refetch: () => Promise.resolve(undefined),
  user: () => undefined,
  defaultProjectId: () => undefined,
};

const defaultTenantsStore: TenantsStore = {
  refetch: () => Promise.resolve([]),
  tenants: () => [],
  persistSelection: () => Promise.resolve(),
};

// Wire provides access to dependencies, global state, and the ability to
// interact with domains/business-logic.
export interface Wire {
  /** Set only on wires from `newWire()` inside WireProvider; absent on `defaultWire`. */
  wireInitialized?: true;
  auth: AuthService;
  grants: GrantsService;
  device: DeviceService;
  chat: ChatService;
  chatTitle: ChatTitleService;
  connectivity: ConnectivityService;
  stream: StreamingService;
  billing: BillingService;
  projectBilling: ProjectBillingService;
  notifications: NotificationsService;
  locale: LocaleService;
  logger: Logger;
  ui: UIStore;
  knowledgeUi: KnowledgeUIService;
  projects: ProjectsService;
  user: UserStore;
  tenants: TenantsStore;
  activeContext: ActiveContextStore;
  /** URL-first org id; falls back to `/context` for project-driven routes. */
  currentOrgId: () => string | undefined;
  /**
   * `currentOrgId`, but only when the user owns that org at org scope. The
   * redirect target for home/fallback navigation to the owner-only `/orgs/{id}`
   * surface; returns undefined otherwise so callers fall through to a safe
   * destination (ENG-6478).
   */
  homeOrgId: () => string | undefined;
  /** URL-first project id; falls back to `/context` on routes without `:projectId`. */
  currentProjectId: () => string | undefined;
  primaryOrganizationId: () => string | undefined;
  /** The tenant the plan, balance and entitlements on screen belong to. */
  currentTenant: () => TenantSummary | undefined;
  userContext: UserContextService;
  labels: LabelsService;
  navigation: NavigationService;
  views: ViewsService;
  assets: AssetsService;
  concepts: ConceptsService;
  rels: RelsService;
  memory: MemoryService;
  devices: DevicesService;
  liveSessions: LiveSessionsService;
  skills: SkillsService;
  tokens: TokensService;
  storedPrompts: StoredPromptsService;
  suggestions: SuggestionsService;
  actions: ActionsService;
  artifacts: ArtifactsService;
  featureGate: FeatureGateService;
  recentChats: RecentChatsService;
}

/** defaultWire signals that the wire is not yet initialized. */
export const defaultWire: Wire = Object.freeze({
  auth: {} as AuthService,
  grants: {} as GrantsService,
  ui: {} as UIStore,
  chat: {} as ChatService,
  chatTitle: {} as ChatTitleService,
  connectivity: {} as ConnectivityService,
  billing: {} as BillingService,
  projectBilling: {} as ProjectBillingService,
  notifications: {} as NotificationsService,
  stream: {} as StreamingService,
  device: {} as DeviceService,
  knowledgeUi: {} as KnowledgeUIService,
  locale: {} as LocaleService,
  logger: new NoopLogger(),
  projects: {} as ProjectsService,
  user: defaultUserStore,
  tenants: defaultTenantsStore,
  activeContext: defaultActiveContext,
  currentOrgId: () => undefined,
  homeOrgId: () => undefined,
  currentProjectId: () => undefined,
  primaryOrganizationId: () => undefined,
  currentTenant: () => undefined,
  userContext: defaultUserContext,
  labels: {} as LabelsService,
  navigation: {
    state: () => ({
      projectId: undefined,
      primaryId: undefined,
      sideId: undefined,
      sideHidden: false,
      edit: false,
      editBlock: undefined,
    }),
  } as NavigationService,
  views: {} as ViewsService,
  assets: {} as AssetsService,
  concepts: {} as ConceptsService,
  rels: {} as RelsService,
  memory: {} as MemoryService,
  devices: {} as DevicesService,
  liveSessions: {} as LiveSessionsService,
  skills: {} as SkillsService,
  tokens: {} as TokensService,
  storedPrompts: {} as StoredPromptsService,
  suggestions: {} as SuggestionsService,
  actions: {} as ActionsService,
  artifacts: {} as ArtifactsService,
  featureGate: {} as FeatureGateService,
  recentChats: {} as RecentChatsService,
});
